import React from 'react';

import type { ForwardRefComponent } from '@coursera/cds-common';

import { BaseHeadingGroup } from '@core/dialogs/common/BaseHeadingGroup';
import type { BaseHeadingGroupProps } from '@core/dialogs/common/BaseHeadingGroup';

import { useDialogContext } from './DialogContextProvider';
import {
  getHeadingGroupCss,
  isCloseAbsoluteOnMobile,
  isCloseAlwaysAbsolute,
} from './styles';

type HeadingGroupType = ForwardRefComponent<HTMLDivElement, Props>;

type BaseProps = Omit<BaseHeadingGroupProps, 'color' | 'heading'>;

export type Props = BaseProps & {
  /**
   * The text to display as the dialog heading
   */
  children: React.ReactText;
};

const HeadingGroup: HeadingGroupType = React.forwardRef(function HeadingGroup(
  props: Props,
  ref: React.Ref<HTMLDivElement>
) {
  const { children, id, ...rest } = props;

  const {
    invert,
    color,
    variant,
    headingGroupId,
    sticky,
    alignment,
    slotOrientation,
    width,
  } = useDialogContext();

  const hasSlot = slotOrientation !== undefined;

  return (
    <BaseHeadingGroup
      ref={ref}
      color={invert ? 'invertBody' : 'body'}
      css={getHeadingGroupCss({
        color,
        variant,
        width,
        sticky: sticky === 'headingGroup',
        alignment,
        hasSlot,
        slotOrientation,
        isCloseAbsolute: isCloseAlwaysAbsolute(slotOrientation),
        isCloseAbsoluteOnMobile: isCloseAbsoluteOnMobile(slotOrientation),
      })}
      heading={children}
      id={id || headingGroupId}
      {...rest}
    />
  );
});

export default HeadingGroup;
